import { atom } from "jotai";
import { conversationAtom, INITIAL_PAGE_CONST } from "./conversation.atoms";
import { Conversation } from "@/core/entities/conversation";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { ConversationRepository } from "@/infrastructure/repositories/conversation/Conversation.repository";
import { GetConversationsUseCase } from "@/core/usecases/conversation";
import { GetConversationsParams } from "@/core/repositories/conversation";
import { drawerAtom } from "../deduction/drawer";
import { createLogger } from "@/infrastructure/services/logger";

const conversationRepository = new ConversationRepository(GraphQlAdapter.getInstance().getClient());
const getConversations = new GetConversationsUseCase(conversationRepository);
const logger = createLogger("conversation.actions");

// Action to set search query
export const setSearchQueryAtom = atom(null, (_get, set, searchQuery: string | null) => {
  set(conversationAtom, prev => ({
    ...prev,
    searchQuery,
  }));
});

// Action to set conversations
export const setConversationsAtom = atom(null, (_get, set, conversations: Conversation[]) => {
  set(conversationAtom, prev => ({
    ...prev,
    conversations,
  }));
});

// Action to append conversations (for pagination)
export const appendConversationsAtom = atom(null, (_get, set, newConversations: Conversation[]) => {
  set(conversationAtom, prev => ({
    ...prev,
    conversations: [...prev.conversations, ...newConversations],
  }));
});

// Action to set total
export const setTotalAtom = atom(null, (_get, set, total: number) => {
  set(conversationAtom, prev => ({
    ...prev,
    total,
  }));
});

// Action to set page
export const setPageAtom = atom(null, (_get, set, page: number) => {
  set(conversationAtom, prev => ({
    ...prev,
    page,
  }));
});

// Action to set limit
export const setLimitAtom = atom(null, (_get, set, limit: number) => {
  set(conversationAtom, prev => ({
    ...prev,
    limit,
  }));
});

// Action to set current conversation
export const setCurrentConversationAtom = atom(null, (_get, set, currentConversation: Conversation | null) => {
  set(conversationAtom, prev => ({
    ...prev,
    currentConversation,
  }));
});

// Action to set loading state
export const setIsLoadingAtom = atom(null, (_get, set, isLoading: boolean) => {
  set(conversationAtom, prev => ({
    ...prev,
    isLoading,
  }));
});

// Action to reset page to initial
export const resetPageAtom = atom(null, (_get, set) => {
  set(conversationAtom, prev => ({
    ...prev,
    page: INITIAL_PAGE_CONST,
  }));
});

// Action to set new message count
export const incrementNewMessageCountAtom = atom(null, (get, set) => {
  const prevNewMessageCount = get(conversationAtom).newMessageCount;
  set(conversationAtom, prev => ({
    ...prev,
    newMessageCount: prevNewMessageCount + 1,
  }));
});

// Action to reset new message count
export const resetNewMessageCountAtom = atom(null, (_get, set) => {
  set(conversationAtom, prev => {
    return {
      ...prev,
      newMessageCount: 0,
    };
  });
});

// Action to fetch conversations
export const fetchConversationsAtom = atom(null, async (get, set) => {
  const state = get(conversationAtom);

  set(conversationAtom, prev => ({
    ...prev,
    isLoading: true,
  }));

  try {
    const params: GetConversationsParams = {
      page_number: state.page,
      page_size: state.limit,
      search_query: state.searchQuery ?? "",
    };

    const result = await getConversations.execute(params);
    const currentPage = state.page;

    set(conversationAtom, prev => {
      if (currentPage === INITIAL_PAGE_CONST) {
        return {
          ...prev,
          conversations: result.conversations,
          total: result.total,
          isLoading: false,
        };
      } else {
        // Deduplicate conversations by ID to prevent duplicates when appending
        const existingIds = new Set(prev.conversations.map(conversation => conversation.id));
        const newConversations = result.conversations.filter(conversation => !existingIds.has(conversation.id));

        return {
          ...prev,
          conversations: [...prev.conversations, ...newConversations],
          total: result.total,
          isLoading: false,
        };
      }
    });
  } catch (error) {
    logger.error("Error fetching conversations:", error);
    set(conversationAtom, prev => ({
      ...prev,
      conversations: [],
      isLoading: false,
    }));
  }
});

// Add/Update and sort conversation when Notification event is received
export const addOrUpdateConversationAtom = atom(null, (get, set, conversation: Conversation) => {
  const drawerState = get(drawerAtom);
  const prevConversations = get(conversationAtom).conversations;

  if (prevConversations.length === 0) {
    return;
  }

  set(conversationAtom, prev => {
    const existingConversationIndex = prev.conversations.findIndex(con => con.id === conversation.id);
    const existingConversation = prev.conversations[existingConversationIndex];
    let updatedConversations: Conversation[];

    if (existingConversationIndex === -1) {
      // Add new conversation
      updatedConversations = [...prev.conversations, conversation];
    } else {
      // Update existing conversation
      updatedConversations = [...prev.conversations];
      updatedConversations[existingConversationIndex] = new Conversation({
        id: existingConversation.id,
        checkNumber: existingConversation.checkNumber,
        invoiceNumber: existingConversation.invoiceNumber,
        referenceId: existingConversation.referenceId,
        categoryKey: existingConversation.categoryKey,
        remittanceItemId: existingConversation.remittanceItemId,
        categoryTypeKey: existingConversation.categoryTypeKey,
        purchaserKey: existingConversation.purchaserKey,
        purchaserName: existingConversation.purchaserName,
        createdAt: existingConversation.createdAt,
        updatedAt: conversation.updatedAt,
        unreadMessageCount:
          drawerState.drawerInfo?.invoiceNumber === conversation.invoiceNumber
            ? 0
            : existingConversation.unreadMessageCount + 1,
        participants: existingConversation.participants,
        lastMessage: conversation.lastMessage,
      });
    }

    // Sort by updatedAt timestamp (most recent first)
    updatedConversations.sort((a, b) => {
      const dateA = new Date(a.updatedAt || 0).getTime();
      const dateB = new Date(b.updatedAt || 0).getTime();
      return dateB - dateA;
    });

    return {
      ...prev,
      conversations: updatedConversations,
    };
  });
});
